import { supabase } from '../supabaseClient';
import { PresentationTemplate } from '../types';
import {
    DEFAULT_MASTER_PRESENTATION_TEMPLATES,
    MASTER_PRESENTATION_TEMPLATES_STORAGE_KEY,
} from '../constants/masterPresentationTemplates';

// Storage key generator for project-specific presentations
const getProjectTemplatesKey = (projectId: string) => `klydapp_presentation_templates_${projectId}`;

/**
 * Validates whether a string is a standard RFC4122 UUID
 */
export const isValidUuid = (val?: string): boolean => {
    return Boolean(val && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val));
};

/**
 * Get project-specific presentation templates stored in localStorage.
 */
export function getLocalProjectTemplates(projectId: string): PresentationTemplate[] {
    if (!projectId || projectId === 'global') return [];
    try {
        const raw = localStorage.getItem(getProjectTemplatesKey(projectId));
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed;
            }
        }
    } catch (e) {
        console.error(`Error reading presentation templates for project ${projectId} from localStorage:`, e);
    }
    return [];
}

/**
 * Save or update a presentation template in localStorage for a specific project.
 */
export function saveLocalProjectTemplate(template: PresentationTemplate): void {
    if (!template.projectId || template.projectId === 'global') return;
    try {
        const current = getLocalProjectTemplates(template.projectId);
        const index = current.findIndex(t => t.id === template.id);
        let updated: PresentationTemplate[];
        if (index >= 0) {
            updated = [...current];
            updated[index] = template;
        } else {
            updated = [template, ...current];
        }
        localStorage.setItem(getProjectTemplatesKey(template.projectId), JSON.stringify(updated));
    } catch (e) {
        console.error(`Error saving presentation template to localStorage for project ${template.projectId}:`, e);
    }
}

/**
 * Delete a presentation template from localStorage for a specific project.
 */
export function deleteLocalProjectTemplate(id: string, projectId: string): void {
    if (!projectId || projectId === 'global') return;
    try {
        const current = getLocalProjectTemplates(projectId);
        const filtered = current.filter(t => t.id !== id);
        localStorage.setItem(getProjectTemplatesKey(projectId), JSON.stringify(filtered));
    } catch (e) {
        console.error(`Error deleting presentation template from localStorage for project ${projectId}:`, e);
    }
}

/**
 * Fetch all presentation templates for a given project, plus global master templates.
 * Merges local offline cache with Supabase records.
 */
export async function fetchTemplates(projectId: string): Promise<PresentationTemplate[]> {
    const localProjectTemplates = getLocalProjectTemplates(projectId);
    const localMasters = getLocalMasterTemplates();

    try {
        const { data, error } = await supabase
            .from('presentation_templates')
            .select('*')
            .or(`project_id.eq.${projectId},project_id.eq.global`)
            .order('updated_at', { ascending: false });

        if (!error && data) {
            const fetched = data.map(mapDbToTemplate);
            
            // Sync any fetched project templates to local cache
            fetched.filter(t => t.projectId === projectId).forEach(t => {
                saveLocalProjectTemplate(t);
            });

            // Combine project templates (remote + any local-only) with master templates
            const combinedProjectMap = new Map<string, PresentationTemplate>();
            // First add local
            localProjectTemplates.forEach(t => combinedProjectMap.set(t.id, t));
            // Then overwrite/add with remote
            fetched.filter(t => t.projectId === projectId).forEach(t => combinedProjectMap.set(t.id, t));

            const projectList = Array.from(combinedProjectMap.values());
            const remoteMasters = fetched.filter(t => t.projectId === 'global');
            const mastersList = remoteMasters.length > 0 ? remoteMasters : localMasters;

            return [...projectList, ...mastersList];
        } else if (error) {
            console.warn('Could not load presentation templates from Supabase, falling back to local cache:', error);
        }
    } catch (err) {
        console.warn('Network error fetching presentation templates from Supabase, using local cache:', err);
    }

    // Fallback: local project templates + local master templates
    return [...localProjectTemplates, ...localMasters];
}

/**
 * Fetch only global master presentation templates.
 */
export async function fetchMasterTemplates(): Promise<PresentationTemplate[]> {
    try {
        const { data, error } = await supabase
            .from('presentation_templates')
            .select('*')
            .eq('project_id', 'global')
            .order('updated_at', { ascending: false });

        if (!error && data && data.length > 0) {
            return data.map(mapDbToTemplate);
        }
    } catch (e) {
        console.warn('Could not load master templates from Supabase, falling back to local storage:', e);
    }

    return getLocalMasterTemplates();
}

/**
 * Get local master templates from localStorage with fallback to built-in defaults.
 */
export function getLocalMasterTemplates(): PresentationTemplate[] {
    try {
        const raw = localStorage.getItem(MASTER_PRESENTATION_TEMPLATES_STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed;
            }
        }
    } catch (e) {
        console.error('Error reading master templates from localStorage', e);
    }
    // Initialize with defaults
    try {
        localStorage.setItem(MASTER_PRESENTATION_TEMPLATES_STORAGE_KEY, JSON.stringify(DEFAULT_MASTER_PRESENTATION_TEMPLATES));
    } catch {}
    return DEFAULT_MASTER_PRESENTATION_TEMPLATES;
}

/**
 * Save master template (upserts in localStorage and attempts Supabase).
 */
export async function saveMasterTemplate(template: PresentationTemplate): Promise<PresentationTemplate> {
    const templateToSave: PresentationTemplate = {
        ...template,
        projectId: 'global',
        updatedAt: new Date().toISOString(),
    };

    // 1. Update localStorage
    const current = getLocalMasterTemplates();
    const index = current.findIndex(t => t.id === templateToSave.id);
    let updated: PresentationTemplate[];
    if (index >= 0) {
        updated = [...current];
        updated[index] = templateToSave;
    } else {
        updated = [templateToSave, ...current];
    }
    try {
        localStorage.setItem(MASTER_PRESENTATION_TEMPLATES_STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
        console.error('Failed to save master template to localStorage', e);
    }

    // 2. Sync to Supabase
    try {
        await saveTemplate(templateToSave);
    } catch (err) {
        console.warn('Syncing master template to Supabase failed, saved locally:', err);
    }

    return templateToSave;
}

/**
 * Delete a master template (from localStorage and Supabase).
 */
export async function deleteMasterTemplate(id: string): Promise<boolean> {
    const current = getLocalMasterTemplates();
    const filtered = current.filter(t => t.id !== id);
    try {
        localStorage.setItem(MASTER_PRESENTATION_TEMPLATES_STORAGE_KEY, JSON.stringify(filtered));
    } catch (e) {
        console.error('Failed to delete master template from localStorage', e);
    }

    try {
        await deleteTemplate(id);
    } catch (err) {
        console.warn('Syncing delete to Supabase failed:', err);
    }

    return true;
}

/**
 * Reset master templates to default standard corporate templates.
 */
export function resetMasterTemplatesToDefault(): PresentationTemplate[] {
    try {
        localStorage.setItem(MASTER_PRESENTATION_TEMPLATES_STORAGE_KEY, JSON.stringify(DEFAULT_MASTER_PRESENTATION_TEMPLATES));
    } catch {}
    return DEFAULT_MASTER_PRESENTATION_TEMPLATES;
}

/**
 * Fetch a single presentation template by ID.
 */
export async function fetchTemplate(id: string, projectId?: string): Promise<PresentationTemplate | null> {
    // Check local project storage first if projectId given
    if (projectId) {
        const local = getLocalProjectTemplates(projectId).find(t => t.id === id);
        if (local) return local;
    }
    // Check master templates
    const localMaster = getLocalMasterTemplates().find(t => t.id === id);
    if (localMaster) return localMaster;

    try {
        const { data, error } = await supabase
            .from('presentation_templates')
            .select('*')
            .eq('id', id)
            .single();

        if (!error && data) {
            return mapDbToTemplate(data);
        }
    } catch (e) {
        console.error('Error fetching presentation template from Supabase:', e);
    }

    return null;
}

/**
 * Save (upsert) a presentation template with guaranteed local persistence and Supabase sync.
 */
export async function saveTemplate(template: PresentationTemplate): Promise<PresentationTemplate | null> {
    // Ensure we have a valid UUID for Supabase if saving to database
    const validUuid = isValidUuid(template.id)
        ? template.id
        : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : undefined);

    const templateToSave: PresentationTemplate = {
        ...template,
        id: validUuid || template.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `tpl-${Date.now()}`),
        updatedAt: new Date().toISOString(),
    };

    // 1. Guaranteed Local Persistence: always store in localStorage first
    if (templateToSave.projectId && templateToSave.projectId !== 'global') {
        saveLocalProjectTemplate(templateToSave);
    }

    // 2. Attempt Supabase sync
    try {
        const dbRecord: any = {
            project_id: templateToSave.projectId,
            name: templateToSave.name,
            slides: JSON.parse(JSON.stringify(templateToSave.slides)),
            created_by: templateToSave.createdBy || null,
        };
        // Only provide ID to Supabase if it's a valid UUID
        if (isValidUuid(templateToSave.id)) {
            dbRecord.id = templateToSave.id;
        }

        const { data, error } = await supabase
            .from('presentation_templates')
            .upsert(dbRecord, { onConflict: 'id' })
            .select()
            .single();

        if (!error && data) {
            const synced = mapDbToTemplate(data);
            if (synced.projectId && synced.projectId !== 'global') {
                saveLocalProjectTemplate(synced);
            }
            return synced;
        } else if (error) {
            console.warn('Supabase upsert returned error, saved locally:', error);
        }
    } catch (err) {
        console.warn('Supabase connection error while saving presentation, preserved in local storage:', err);
    }

    // Return the locally saved template so UI never fails
    return templateToSave;
}

/**
 * Delete a presentation template by ID (from local storage and Supabase).
 */
export async function deleteTemplate(id: string, projectId?: string): Promise<boolean> {
    if (projectId) {
        deleteLocalProjectTemplate(id, projectId);
    }

    try {
        if (isValidUuid(id)) {
            const { error } = await supabase
                .from('presentation_templates')
                .delete()
                .eq('id', id);

            if (error) {
                console.warn('Error deleting presentation template from Supabase:', error);
            }
        }
    } catch (err) {
        console.warn('Network error deleting presentation template from Supabase:', err);
    }

    return true;
}

// --- Internal mapper ---
function mapDbToTemplate(row: any): PresentationTemplate {
    return {
        id: row.id,
        projectId: row.project_id,
        name: row.name,
        slides: row.slides || [],
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
    };
}


